/**
 * Workflow Canvas Vuex Module
 *
 * Manages state for the visual workflow builder including:
 * - Node positions and data
 * - Edge connections
 * - Selection state
 * - Canvas viewport (zoom, pan)
 * - Undo/redo history
 */

const state = {
  // Nodes in the workflow (activities)
  nodes: [],

  // Edges connecting nodes
  edges: [],

  // Currently selected node ID
  selectedNodeId: null,

  // Canvas viewport state
  canvasZoom: 1,
  canvasOffset: { x: 0, y: 0 },

  // Undo/redo history
  history: [],
  historyIndex: -1,
  maxHistoryLength: 50,

  // Dirty flag for unsaved changes
  isDirty: false,

  // Loading state
  isLoading: false
}

const getters = {
  // Get all nodes
  allNodes: (state) => state.nodes,

  // Get all edges
  allEdges: (state) => state.edges,

  // Get selected node
  selectedNode: (state) => {
    if (!state.selectedNodeId) return null
    return state.nodes.find(n => n.id === state.selectedNodeId)
  },

  // Get nodes connected to a specific node
  connectedNodes: (state) => (nodeId) => {
    const edges = state.edges.filter(e => e.source === nodeId || e.target === nodeId)
    const connectedIds = edges.flatMap(e => [e.source, e.target]).filter(id => id !== nodeId)
    return state.nodes.filter(n => connectedIds.includes(n.id))
  },

  // Check if can undo
  canUndo: (state) => state.historyIndex > 0,

  // Check if can redo
  canRedo: (state) => state.historyIndex < state.history.length - 1,

  // Get trigger node (start of workflow)
  triggerNode: (state) => state.nodes.find(n => n.node_type === 'trigger'),

  // Get condition nodes
  conditionNodes: (state) => state.nodes.filter(n => n.node_type === 'condition'),

  // Get action nodes
  actionNodes: (state) => state.nodes.filter(n => n.node_type === 'action'),

  // Check if workflow has unsaved changes
  hasUnsavedChanges: (state) => state.isDirty
}

const mutations = {
  // Set nodes
  SET_NODES(state, nodes) {
    state.nodes = nodes
  },

  // Add a node
  ADD_NODE(state, node) {
    state.nodes.push(node)
    state.isDirty = true
  },

  // Update a node
  UPDATE_NODE(state, { id, data }) {
    const index = state.nodes.findIndex(n => n.id === id)
    if (index > -1) {
      state.nodes[index] = { ...state.nodes[index], ...data }
      state.isDirty = true
    }
  },

  // Remove a node
  REMOVE_NODE(state, nodeId) {
    state.nodes = state.nodes.filter(n => n.id !== nodeId)
    // Also remove edges connected to this node
    state.edges = state.edges.filter(e => e.source !== nodeId && e.target !== nodeId)
    state.isDirty = true
  },

  // Set edges
  SET_EDGES(state, edges) {
    state.edges = edges
  },

  // Add an edge
  ADD_EDGE(state, edge) {
    state.edges.push(edge)
    state.isDirty = true
  },

  // Remove an edge
  REMOVE_EDGE(state, { source, target }) {
    state.edges = state.edges.filter(e => !(e.source === source && e.target === target))
    state.isDirty = true
  },

  // Set selected node
  SET_SELECTED_NODE(state, nodeId) {
    state.selectedNodeId = nodeId
  },

  // Set canvas zoom
  SET_CANVAS_ZOOM(state, zoom) {
    state.canvasZoom = zoom
  },

  // Set canvas offset
  SET_CANVAS_OFFSET(state, offset) {
    state.canvasOffset = offset
  },

  // Add history entry
  ADD_HISTORY(state) {
    // Remove any future history if we're not at the end
    if (state.historyIndex < state.history.length - 1) {
      state.history = state.history.slice(0, state.historyIndex + 1)
    }

    // Add current state to history
    state.history.push({
      nodes: JSON.parse(JSON.stringify(state.nodes)),
      edges: JSON.parse(JSON.stringify(state.edges))
    })

    // Trim history if too long
    if (state.history.length > state.maxHistoryLength) {
      state.history.shift()
    } else {
      state.historyIndex++
    }
  },

  // Set history index (for undo/redo)
  SET_HISTORY_INDEX(state, index) {
    state.historyIndex = index
  },

  // Restore from history
  RESTORE_FROM_HISTORY(state, index) {
    const entry = state.history[index]
    if (entry) {
      state.nodes = JSON.parse(JSON.stringify(entry.nodes))
      state.edges = JSON.parse(JSON.stringify(entry.edges))
      state.historyIndex = index
    }
  },

  // Set dirty flag
  SET_DIRTY(state, isDirty) {
    state.isDirty = isDirty
  },

  // Set loading state
  SET_LOADING(state, isLoading) {
    state.isLoading = isLoading
  },

  // Clear all state
  CLEAR(state) {
    state.nodes = []
    state.edges = []
    state.selectedNodeId = null
    state.history = []
    state.historyIndex = -1
    state.isDirty = false
  }
}

const actions = {
  // Initialize workflow from activities
  initFromActivities({ commit }, activities) {
    const nodes = activities.map(activity => ({
      id: activity.id,
      node_type: activity.node_type || 'action',
      type: activity.type,
      title: activity.title,
      position_x: activity.position_x || 0,
      position_y: activity.position_y || 0,
      data: activity
    }))

    // Build edges from dependencies
    const edges = []
    activities.forEach(activity => {
      if (activity.after_task_id) {
        edges.push({
          source: activity.after_task_id,
          target: activity.id,
          type: 'default'
        })
      }
      if (activity.branch_yes_id) {
        edges.push({
          source: activity.id,
          target: activity.branch_yes_id,
          type: 'yes'
        })
      }
      if (activity.branch_no_id) {
        edges.push({
          source: activity.id,
          target: activity.branch_no_id,
          type: 'no'
        })
      }
    })

    commit('SET_NODES', nodes)
    commit('SET_EDGES', edges)
    commit('ADD_HISTORY')
  },

  // Add a new node
  addNode({ commit, dispatch }, node) {
    commit('ADD_NODE', node)
    dispatch('saveHistory')
  },

  // Update node data
  updateNode({ commit, dispatch }, payload) {
    commit('UPDATE_NODE', payload)
    dispatch('saveHistory')
  },

  // Remove a node
  removeNode({ commit, dispatch }, nodeId) {
    commit('REMOVE_NODE', nodeId)
    dispatch('saveHistory')
  },

  // Add a connection
  addEdge({ commit, dispatch }, edge) {
    commit('ADD_EDGE', edge)
    dispatch('saveHistory')
  },

  // Remove a connection
  removeEdge({ commit, dispatch }, payload) {
    commit('REMOVE_EDGE', payload)
    dispatch('saveHistory')
  },

  // Select a node
  selectNode({ commit }, nodeId) {
    commit('SET_SELECTED_NODE', nodeId)
  },

  // Update canvas viewport
  updateViewport({ commit }, { zoom, offset }) {
    if (zoom !== undefined) commit('SET_CANVAS_ZOOM', zoom)
    if (offset !== undefined) commit('SET_CANVAS_OFFSET', offset)
  },

  // Save current state to history
  saveHistory({ commit }) {
    commit('ADD_HISTORY')
  },

  // Undo
  undo({ commit, state }) {
    if (state.historyIndex > 0) {
      commit('RESTORE_FROM_HISTORY', state.historyIndex - 1)
    }
  },

  // Redo
  redo({ commit, state }) {
    if (state.historyIndex < state.history.length - 1) {
      commit('RESTORE_FROM_HISTORY', state.historyIndex + 1)
    }
  },

  // Mark as saved
  markAsSaved({ commit }) {
    commit('SET_DIRTY', false)
  },

  // Clear workflow
  clearWorkflow({ commit }) {
    commit('CLEAR')
  },

  // Export workflow to activities format
  exportToActivities({ state }) {
    return state.nodes.map(node => ({
      ...node.data,
      id: node.id,
      node_type: node.node_type,
      position_x: node.position_x,
      position_y: node.position_y,
      after_task_id: state.edges.find(e => e.target === node.id && e.type === 'default')?.source || null,
      branch_yes_id: state.edges.find(e => e.source === node.id && e.type === 'yes')?.target || null,
      branch_no_id: state.edges.find(e => e.source === node.id && e.type === 'no')?.target || null
    }))
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
